import React, { useState, useEffect } from 'react';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';
import {
  ChevronRight, BookOpen, Bell, Zap, AlertTriangle, CheckCircle,
  Copy, Check, Clock, Ban, Shield, Webhook, RefreshCcw,
  Mail, Globe, Code, ArrowRight, ChevronDown, Settings,
  AlertOctagon, Server
} from 'lucide-react';

const SIDEBAR = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'notification-levels', label: 'Notification Levels', icon: Settings },
  { id: 'default-notifications', label: 'Default Notifications', icon: Bell },
  { id: 'callback-urls', label: 'Callback URLs', icon: Globe },
  { id: 'webhook-security', label: 'Webhook Security', icon: Shield },
  { id: 'retry-schedule', label: 'Retry Schedule', icon: RefreshCcw },
  { id: 'payload-structure', label: 'Payload Structure', icon: Code },
  { id: 'event-types', label: 'Event Types', icon: Zap },
  { id: 'implementation', label: 'Implementation Examples', icon: Server },
  { id: 'best-practices', label: 'Best Practices', icon: CheckCircle },
  { id: 'common-mistakes', label: 'Common Mistakes', icon: AlertOctagon },
];

function CodeBlock({ code, lang = 'json', title }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl border border-border overflow-hidden my-4">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
          <span className="text-xs font-mono text-muted-foreground">{title}</span>
          <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            {copied ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
          </button>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-sm bg-[#0d1117] text-gray-300" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', title, children }) {
  const styles = {
    info: 'border-primary/30 bg-primary/5',
    warning: 'border-amber-500/30 bg-amber-500/5',
    success: 'border-emerald-500/30 bg-emerald-500/5',
    danger: 'border-red-500/30 bg-red-500/5',
  };
  const icons = { info: Zap, warning: AlertTriangle, success: CheckCircle, danger: Ban };
  const Icon = icons[type];
  const textColor = { info: 'text-primary', warning: 'text-amber-400', success: 'text-emerald-400', danger: 'text-red-400' };
  return (
    <div className={`rounded-xl border p-4 my-4 ${styles[type]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${textColor[type]}`} />
        <span className={`text-sm font-semibold ${textColor[type]}`}>{title}</span>
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 pb-12 mb-12 border-b border-border last:border-0">
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
      <div className="prose-content space-y-4 text-[15px] text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function StepFlow({ steps }) {
  return (
    <div className="my-6 space-y-0">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">{i + 1}</div>
            {i < steps.length - 1 && <div className="w-px h-full bg-border min-h-[24px]" />}
          </div>
          <div className="pb-6">
            <p className="text-sm font-semibold text-foreground">{step.title}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{step.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const DocsNotificationsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = 'Payment Notifications | CoinPayUp Docs';
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          break;
        }
      }
    }, { rootMargin: '-100px 0px -60% 0px', threshold: 0 });
    SIDEBAR.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="docs-notifications-page">
      <SiteHeader />

      {/* Page Header */}
      <div className="border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <span>Docs</span><ChevronRight className="h-3.5 w-3.5" /><span>Guides</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-foreground">Payment Notifications</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Payment Notifications
          </h1>
          <p className="text-base text-muted-foreground mt-2 max-w-2xl">
            How CoinPayUp keeps merchants informed about payment activity — from simple email alerts to enterprise-grade webhook integrations.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex gap-8 relative">
        {/* Mobile sidebar toggle */}
        <button className="lg:hidden fixed bottom-6 right-6 z-40 bg-primary text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium"
          onClick={() => setSidebarOpen(!sidebarOpen)}>
          <BookOpen className="h-4 w-4" /> Sections <ChevronDown className={`h-3.5 w-3.5 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Sidebar */}
        <aside className={`w-64 flex-shrink-0 lg:block ${sidebarOpen ? 'fixed inset-0 z-30 bg-background/95 backdrop-blur-xl lg:relative lg:bg-transparent p-6 pt-20 lg:p-0 overflow-y-auto' : 'hidden'}`}>
          <nav className="lg:sticky lg:top-24 py-8 space-y-1" data-testid="docs-notifications-sidebar">
            {SIDEBAR.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} data-testid={`sidebar-${item.id}`}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${activeSection === item.id ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 py-8 lg:py-12 max-w-3xl" data-testid="docs-notifications-content">

          {/* OVERVIEW */}
          <Section id="overview" title="Overview">
            <p>
              CoinPayUp provides a <strong className="text-foreground">multi-layered notification system</strong> that keeps merchants and their systems synchronized with every payment event. Whether you're a solo merchant needing simple email receipts or an enterprise platform requiring real-time webhook integrations, CoinPayUp has you covered.
            </p>
            <p>This guide covers:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Notification levels: from zero-code to full API integration</li>
              <li>Built-in redirect and email notifications</li>
              <li>Callback URL configuration for payment flows</li>
              <li>Webhook security, signing, and verification</li>
              <li>Retry logic and delivery guarantees</li>
              <li>Payload structure and event type reference</li>
              <li>Production-ready implementation examples</li>
              <li>Best practices and common mistakes to avoid</li>
            </ul>
          </Section>

          {/* NOTIFICATION LEVELS */}
          <Section id="notification-levels" title="Notification Levels">
            <p>
              CoinPayUp offers <strong className="text-foreground">three notification levels</strong>, each designed for different integration needs. Choose the level that matches your technical capabilities and business requirements.
            </p>

            {/* Beginner */}
            <div className="rounded-xl border border-border p-5 my-6 bg-emerald-500/5 border-emerald-500/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Beginner Level</h3>
                  <span className="text-xs text-emerald-400 font-medium">No coding required</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Designed for non-technical merchants who want instant payment notifications without writing any code.</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                <li><strong className="text-foreground">Automatic browser redirects</strong> — customers are redirected to your website after payment</li>
                <li><strong className="text-foreground">Email payment confirmations</strong> — receive an email for every payment received</li>
                <li><strong className="text-foreground">Dashboard notifications</strong> — all payment activity visible in the CoinPayUp dashboard</li>
              </ul>
              <div className="mt-3 px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <strong className="text-foreground">Ideal for:</strong> Small businesses, freelancers, single-product stores, or anyone who processes payments manually.
              </div>
            </div>

            {/* Standard */}
            <div className="rounded-xl border border-border p-5 my-6 bg-primary/5 border-primary/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Globe className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Standard Level</h3>
                  <span className="text-xs text-primary font-medium">Recommended for eCommerce</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Designed for typical eCommerce websites that need automated order fulfillment after payment.</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                <li><strong className="text-foreground">Success & cancel URL redirects</strong> — customers land on your thank-you or retry page</li>
                <li><strong className="text-foreground">Webhook notifications</strong> — your server receives a <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs font-mono">POST</code> request when payment is confirmed</li>
                <li><strong className="text-foreground">Email notifications</strong> — payment receipts sent to both merchant and customer</li>
              </ul>
              <div className="mt-3 px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <strong className="text-foreground">Ideal for:</strong> Online stores, SaaS websites, service providers with checkout flows. Covers 90% of integration needs.
              </div>
            </div>

            {/* Advanced */}
            <div className="rounded-xl border border-border p-5 my-6 bg-amber-500/5 border-amber-500/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Code className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">Advanced Level</h3>
                  <span className="text-xs text-amber-400 font-medium">Full API integration</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Designed for custom integrations, SaaS platforms, and businesses that require granular control over every payment event.</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                <li><strong className="text-foreground">Real-time webhooks</strong> — receive events for every status change as it happens</li>
                <li><strong className="text-foreground">Backend invoice status sync</strong> — poll or listen for invoice state transitions</li>
                <li><strong className="text-foreground">Partial payment updates</strong> — track incremental payments in real-time</li>
                <li><strong className="text-foreground">Late payment alerts</strong> — get notified when payments arrive after expiry</li>
                <li><strong className="text-foreground">Custom retry handling</strong> — configure retry behavior and failure notifications</li>
              </ul>
              <div className="mt-3 px-3 py-2 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <strong className="text-foreground">Ideal for:</strong> Payment aggregators, multi-merchant platforms, subscription services, or any system requiring programmatic payment handling.
              </div>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Configuring Your Level</h3>
            <CodeBlock title="PUT /api/notifications/settings" code={`{
  "notification_level": "standard",
  "email_notifications": true,
  "redirect_after_payment": true,
  "success_url": "https://yoursite.com/payment-success",
  "cancel_url": "https://yoursite.com/payment-cancelled"
}`} />
            <p>You can change your notification level at any time from the dashboard under <strong className="text-foreground">Settings &rarr; Notifications</strong>, or via the API.</p>
          </Section>

          {/* DEFAULT NOTIFICATIONS */}
          <Section id="default-notifications" title="Default Notifications">
            <p>CoinPayUp includes built-in notifications that work out of the box — no webhook setup required.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Redirect After Payment</h3>
            <p>When enabled, CoinPayUp automatically redirects the customer's browser after a payment is confirmed or abandoned.</p>

            <StepFlow steps={[
              { title: 'Customer completes payment', desc: 'The checkout page detects payment confirmation via real-time status polling.' },
              { title: 'Success redirect triggered', desc: 'The browser redirects to your configured success_url with the invoice reference appended as a query parameter.' },
              { title: 'Customer lands on your page', desc: 'Your success page displays the order confirmation. You can use the invoice_id to fetch payment details via API.' },
            ]} />

            <CodeBlock title="Success Redirect Example" code={`// Customer's browser redirects to:
https://merchant.com/payment-success?invoice_id=INV-20260221-A1B2C3

// If payment fails or times out:
https://merchant.com/payment-cancelled?invoice_id=INV-20260221-A1B2C3`} />

            <InfoBox type="warning" title="Browser Close Scenario">
              If the customer closes their browser before the redirect fires, the redirect will not occur. However, the payment is still recorded on-chain and CoinPayUp will still:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Mark the invoice as paid in the dashboard</li>
                <li>Send the email notification (if enabled)</li>
                <li>Dispatch webhook events (if configured)</li>
              </ul>
              This is why <strong>webhooks are the recommended method</strong> for order fulfillment — they don't depend on the customer's browser.
            </InfoBox>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Email Notifications</h3>
            <p>CoinPayUp sends automatic email notifications for key payment events. These are enabled by default and can be toggled in your notification settings.</p>

            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Event</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Email Sent</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Recipient</th>
                </tr></thead>
                <tbody>
                  {[
                    ['Payment received', 'Confirmation with amount, TX hash, and invoice details', 'Merchant'],
                    ['Partial payment', 'Alert showing amount received and remaining balance', 'Merchant'],
                    ['Payment confirmed', 'Final confirmation after blockchain confirmations met', 'Merchant + Customer (if email provided)'],
                    ['Invoice expired', 'Expiry notice with any partial amounts received', 'Merchant'],
                    ['Payment failed', 'Alert with failure reason and recommended actions', 'Merchant'],
                  ].map(([event, desc, recipient], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border text-foreground font-medium">{event}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{desc}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{recipient}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CodeBlock title="Enable/Disable Email Notifications" code={`PUT /api/notifications/settings

{
  "email_notifications": true    // set to false to disable
}`} />
          </Section>

          {/* CALLBACK URLS */}
          <Section id="callback-urls" title="Callback URLs">
            <p>Callback URLs control where the customer's browser is redirected after a payment flow. They provide a seamless checkout experience and allow you to display order confirmations or retry pages.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Success URL</h3>
            <p>The <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">success_url</code> is triggered after a payment is confirmed on-chain. CoinPayUp appends the invoice reference as a query parameter so your page can display the correct order details.</p>
            <CodeBlock title="Success URL Configuration" code={`PUT /api/notifications/settings

{
  "redirect_after_payment": true,
  "success_url": "https://merchant.com/payment-success"
}

// After payment confirmation, customer redirects to:
https://merchant.com/payment-success?invoice_id=INV-20260221-A1B2C3`} />

            <p><strong className="text-foreground">Recommended use cases:</strong></p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Display an order confirmation page with the purchased items</li>
              <li>Show a download link for digital products</li>
              <li>Trigger a "thank you" page with next steps</li>
              <li>Redirect to a receipt or invoice summary page</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Cancel URL</h3>
            <p>The <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">cancel_url</code> is triggered when the payment is not completed — either due to timeout, user cancellation, or invoice expiry.</p>
            <CodeBlock title="Cancel URL Configuration" code={`PUT /api/notifications/settings

{
  "redirect_after_payment": true,
  "cancel_url": "https://merchant.com/payment-cancelled"
}

// If payment is abandoned or times out:
https://merchant.com/payment-cancelled?invoice_id=INV-20260221-A1B2C3`} />

            <InfoBox type="info" title="When Cancel Redirects Trigger">
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Invoice expires</strong> — no payment received before the expiry time</li>
                <li><strong>Customer clicks cancel</strong> — user explicitly abandons the checkout</li>
                <li><strong>Timeout</strong> — the checkout session exceeds the configured time limit</li>
              </ul>
            </InfoBox>

            <InfoBox type="warning" title="Important">
              Never use callback URLs as your sole confirmation of payment. Redirects happen client-side in the customer's browser and can be spoofed, blocked, or interrupted. Always verify payment status server-side using webhooks or the invoice API.
            </InfoBox>
          </Section>

          {/* WEBHOOK SECURITY */}
          <Section id="webhook-security" title="Webhook Security & Verification">
            <p>Webhooks are the most reliable way to receive payment notifications. CoinPayUp signs every webhook request with <strong className="text-foreground">HMAC-SHA256</strong>, allowing you to verify that each notification is authentic and untampered.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Why Webhook Security Matters</h3>
            <p>Without signature verification, an attacker could send fake payment confirmations to your endpoint, tricking your system into fulfilling orders that were never paid for. CoinPayUp's signing mechanism prevents this.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">How Signing Works</h3>
            <StepFlow steps={[
              { title: 'Payload constructed', desc: 'CoinPayUp builds the full webhook payload with event type, data, and timestamp.' },
              { title: 'Payload serialized', desc: 'The JSON payload is serialized with sorted keys to ensure a consistent byte representation.' },
              { title: 'HMAC signature generated', desc: 'The serialized payload is signed using HMAC-SHA256 with your webhook secret key.' },
              { title: 'Request sent with headers', desc: 'The signed request is sent to your endpoint with the signature in the X-CoinPayUp-Signature header.' },
              { title: 'Your server verifies', desc: 'Your server recomputes the HMAC using the same secret and compares it to the received signature.' },
            ]} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Request Headers</h3>
            <CodeBlock title="Headers sent with every webhook" code={`Content-Type: application/json
X-CoinPayUp-Signature: a1b2c3d4e5f6...  (HMAC-SHA256 hex digest)
X-CoinPayUp-Event: payment.confirmed
User-Agent: CoinPayUp-Webhook/1.0`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Signature Verification — Node.js</h3>
            <CodeBlock title="Node.js Verification Example" lang="javascript" code={`const crypto = require('crypto');

function verifyCoinPayUpWebhook(rawBody, receivedSignature, webhookSecret) {
  // 1. Parse and re-serialize with sorted keys (matches CoinPayUp's signing)
  const payload = JSON.parse(rawBody);
  const serialized = JSON.stringify(payload, Object.keys(payload).sort());
  
  // 2. Compute expected signature
  const expected = crypto
    .createHmac('sha256', webhookSecret)
    .update(serialized)
    .digest('hex');
  
  // 3. Timing-safe comparison to prevent timing attacks
  return crypto.timingSafeEqual(
    Buffer.from(receivedSignature),
    Buffer.from(expected)
  );
}

// Usage in Express middleware
app.post('/webhooks/coinpayup', express.raw({ type: 'application/json' }), (req, res) => {
  const signature = req.headers['x-coinpayup-signature'];
  const isValid = verifyCoinPayUpWebhook(req.body.toString(), signature, WEBHOOK_SECRET);
  
  if (!isValid) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  
  // Process the event...
  res.status(200).json({ received: true });
});`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Signature Verification — Python</h3>
            <CodeBlock title="Python Verification Example" lang="python" code={`import hmac
import hashlib
import json

def verify_coinpayup_webhook(raw_body: str, received_signature: str, webhook_secret: str) -> bool:
    """Verify the authenticity of a CoinPayUp webhook."""
    # Parse and re-serialize with sorted keys
    payload = json.loads(raw_body)
    serialized = json.dumps(payload, sort_keys=True)
    
    # Compute expected signature
    expected = hmac.new(
        webhook_secret.encode(),
        serialized.encode(),
        hashlib.sha256
    ).hexdigest()
    
    # Timing-safe comparison
    return hmac.compare_digest(received_signature, expected)

# Usage in FastAPI / Flask
@app.post("/webhooks/coinpayup")
async def handle_webhook(request: Request):
    body = await request.body()
    signature = request.headers.get("X-CoinPayUp-Signature")
    
    if not verify_coinpayup_webhook(body.decode(), signature, WEBHOOK_SECRET):
        raise HTTPException(status_code=401, detail="Invalid signature")
    
    event = json.loads(body)
    # Process the event...
    return {"received": True}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Signature Verification — PHP</h3>
            <CodeBlock title="PHP Verification Example" lang="php" code={`<?php
function verifyCoinPayUpWebhook($rawBody, $receivedSignature, $webhookSecret) {
    // Parse and re-serialize with sorted keys
    $payload = json_decode($rawBody, true);
    ksort($payload);
    $serialized = json_encode($payload);
    
    // Compute expected signature
    $expected = hash_hmac('sha256', $serialized, $webhookSecret);
    
    // Timing-safe comparison
    return hash_equals($expected, $receivedSignature);
}

// Usage
$rawBody = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_COINPAYUP_SIGNATURE'] ?? '';

if (!verifyCoinPayUpWebhook($rawBody, $signature, $webhookSecret)) {
    http_response_code(401);
    die('Invalid signature');
}

$event = json_decode($rawBody, true);
// Process the event...
http_response_code(200);
?>`} />

            <InfoBox type="danger" title="Critical Security Rule">
              Never process a webhook payload without verifying the signature first. Unsigned or improperly verified webhooks can be exploited by attackers to trigger unauthorized order fulfillments, balance credits, or subscription activations.
            </InfoBox>
          </Section>

          {/* RETRY SCHEDULE */}
          <Section id="retry-schedule" title="Retry Schedule">
            <p>When your server fails to respond with a <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">2xx</code> status code, CoinPayUp automatically retries the webhook delivery using <strong className="text-foreground">exponential backoff</strong>.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Retry Behavior</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Attempt</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Delay</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Cumulative Time</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Status</th>
                </tr></thead>
                <tbody>
                  {[
                    ['1 (initial)', 'Immediate', '0s', 'bg-emerald-500/10'],
                    ['2', '1 second', '~1s', 'bg-amber-500/10'],
                    ['3', '2 seconds', '~3s', 'bg-amber-500/10'],
                    ['4', '4 seconds', '~7s', 'bg-amber-500/10'],
                    ['5', '8 seconds', '~15s', 'bg-amber-500/10'],
                    ['6', '16 seconds', '~31s', 'bg-amber-500/10'],
                    ['7 (final)', '32 seconds', '~63s', 'bg-red-500/10'],
                  ].map(([attempt, delay, cumulative, bg], i) => (
                    <tr key={i} className={bg}>
                      <td className="px-4 py-2 border-b border-border font-mono text-foreground text-xs">{attempt}</td>
                      <td className="px-4 py-2 border-b border-border text-muted-foreground">{delay}</td>
                      <td className="px-4 py-2 border-b border-border text-muted-foreground">{cumulative}</td>
                      <td className="px-4 py-2 border-b border-border">
                        {i === 0 ? <span className="text-emerald-400 text-xs font-medium">First delivery</span> :
                         i === 6 ? <span className="text-red-400 text-xs font-medium">Last attempt</span> :
                         <span className="text-amber-400 text-xs font-medium">Retry</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CodeBlock title="Backoff Formula" code={`delay = BACKOFF_FACTOR ^ retry_count

BACKOFF_FACTOR = 2
MAX_RETRIES = 6

Attempt 1: 2^0 = 1 second
Attempt 2: 2^1 = 2 seconds
Attempt 3: 2^2 = 4 seconds
Attempt 4: 2^3 = 8 seconds
Attempt 5: 2^4 = 16 seconds
Attempt 6: 2^5 = 32 seconds

Total retry window: ~63 seconds
After 6 failed retries: webhook marked as permanently failed`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">What Triggers a Retry?</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className="text-foreground">Non-2xx response</strong> — your server returns 4xx or 5xx status code</li>
              <li><strong className="text-foreground">Connection timeout</strong> — your server doesn't respond within 30 seconds</li>
              <li><strong className="text-foreground">Connection refused</strong> — your server is unreachable</li>
              <li><strong className="text-foreground">DNS resolution failure</strong> — the webhook URL cannot be resolved</li>
            </ul>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">When Retries Stop</h3>
            <p>After <strong className="text-foreground">6 failed retries</strong> (7 total attempts), the webhook is marked as permanently failed. The failed delivery is logged in the webhook delivery history, accessible from your dashboard under <strong className="text-foreground">Webhooks &rarr; Delivery Logs</strong>.</p>

            <InfoBox type="info" title="Best Practice: Respond Quickly">
              Your webhook endpoint should respond with <code className="px-1 py-0.5 rounded bg-muted text-foreground text-xs font-mono">HTTP 200</code> as fast as possible — ideally within 5 seconds. Perform heavy processing (order fulfillment, email sending, database updates) asynchronously after responding. This prevents unnecessary retries caused by response timeouts.
            </InfoBox>
          </Section>

          {/* PAYLOAD STRUCTURE */}
          <Section id="payload-structure" title="Payload Structure">
            <p>Every webhook payload follows a consistent structure with three top-level fields: <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">event</code>, <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">data</code>, and <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">timestamp</code>.</p>

            <CodeBlock title="Base Payload Structure" code={`{
  "event": "payment.confirmed",     // Event type identifier
  "data": { ... },                  // Event-specific payload
  "timestamp": "2026-02-21T14:30:00.000Z"  // ISO 8601 UTC timestamp
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">payment.confirmed</h3>
            <p>Sent when a payment transaction is confirmed on the blockchain.</p>
            <CodeBlock title="payment.confirmed payload" code={`{
  "event": "payment.confirmed",
  "data": {
    "invoice_id": "INV-20260221-A1B2C3",
    "tx_hash": "0xabc123def456789...",
    "amount": 100.0,
    "currency": "USDT",
    "confirmations": 12
  },
  "timestamp": "2026-02-21T14:30:00.000Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">invoice.paid</h3>
            <p>Sent when an invoice reaches the <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">paid</code> status — meaning the full amount (or within tolerance) has been received.</p>
            <CodeBlock title="invoice.paid payload" code={`{
  "event": "invoice.paid",
  "data": {
    "invoice_id": "INV-20260221-A1B2C3",
    "amount": 100.0,
    "currency": "USDT",
    "tx_hash": "0xabc123def456789..."
  },
  "timestamp": "2026-02-21T14:32:00.000Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">invoice.underpaid</h3>
            <p>Sent when a partial payment is received that doesn't meet the required amount.</p>
            <CodeBlock title="invoice.underpaid payload" code={`{
  "event": "invoice.underpaid",
  "data": {
    "invoice_id": "INV-20260221-A1B2C3",
    "amount_expected": 100.0,
    "amount_received": 60.0,
    "remaining_amount": 40.0,
    "currency": "USDT",
    "tx_hash": "0xdef789abc123456...",
    "message": "Partial payment received. 40.0 USDT remaining."
  },
  "timestamp": "2026-02-21T14:28:00.000Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">invoice.overpaid</h3>
            <p>Sent when the received amount exceeds the invoice by more than 1%.</p>
            <CodeBlock title="invoice.overpaid payload" code={`{
  "event": "invoice.overpaid",
  "data": {
    "invoice_id": "INV-20260221-A1B2C3",
    "amount": 105.0,
    "currency": "USDT",
    "tx_hash": "0x789abc123def456...",
    "overpaid_by": 5.0
  },
  "timestamp": "2026-02-21T14:35:00.000Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">invoice.expired</h3>
            <p>Sent when an invoice passes its expiry time without receiving full payment.</p>
            <CodeBlock title="invoice.expired payload" code={`{
  "event": "invoice.expired",
  "data": {
    "invoice_id": "INV-20260221-A1B2C3",
    "amount_expected": 100.0,
    "amount_received": 0.0,
    "currency": "USDT",
    "expired_at": "2026-02-21T15:30:00.000Z"
  },
  "timestamp": "2026-02-21T15:30:05.000Z"
}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Field Reference</h3>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Field</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Type</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Description</th>
                </tr></thead>
                <tbody>
                  {[
                    ['event', 'string', 'Event type identifier (e.g., payment.confirmed)'],
                    ['data.invoice_id', 'string', 'Unique invoice reference ID'],
                    ['data.tx_hash', 'string', 'Blockchain transaction hash'],
                    ['data.amount', 'number', 'Payment amount in the invoice currency'],
                    ['data.currency', 'string', 'Token symbol (USDT, USDC, BNB, etc.)'],
                    ['data.confirmations', 'number', 'Number of blockchain confirmations'],
                    ['data.amount_expected', 'number', 'Total invoice amount (for underpaid events)'],
                    ['data.amount_received', 'number', 'Cumulative amount received so far'],
                    ['data.remaining_amount', 'number', 'Amount still owed (for underpaid events)'],
                    ['data.overpaid_by', 'number', 'Excess amount (for overpaid events)'],
                    ['timestamp', 'string', 'ISO 8601 UTC timestamp of event dispatch'],
                  ].map(([field, type, desc], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 border-b border-border font-mono text-primary text-xs">{field}</td>
                      <td className="px-4 py-2 border-b border-border font-mono text-xs text-foreground">{type}</td>
                      <td className="px-4 py-2 border-b border-border text-muted-foreground">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* EVENT TYPES */}
          <Section id="event-types" title="Event Types">
            <p>CoinPayUp dispatches the following webhook events. Subscribe to specific events when configuring your webhook endpoint.</p>

            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead><tr className="bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Event</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">When It Fires</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-foreground border-b border-border">Use Case</th>
                </tr></thead>
                <tbody>
                  {[
                    ['payment.confirmed', 'A blockchain transaction is confirmed for an invoice', 'Update order status, log payment'],
                    ['invoice.paid', 'Invoice is fully paid (exact or within tolerance)', 'Fulfill order, activate subscription'],
                    ['invoice.underpaid', 'Payment received but below required amount', 'Notify customer of remaining balance'],
                    ['invoice.overpaid', 'Payment exceeds invoice amount by > 1%', 'Flag for manual review or auto-refund'],
                    ['invoice.expired', 'Invoice passes expiry time without full payment', 'Cancel pending order, release inventory'],
                  ].map(([event, trigger, useCase], i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 border-b border-border"><code className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-foreground">{event}</code></td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{trigger}</td>
                      <td className="px-4 py-2.5 border-b border-border text-muted-foreground">{useCase}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Event Flow Timeline</h3>
            <CodeBlock title="Typical Payment Event Sequence" code={`Customer sends 100 USDT for a 100 USDT invoice:

T+0s    Invoice created (status: pending)
T+15s   Transaction detected on mempool
T+30s   Transaction confirmed (1 block)
        → payment.confirmed dispatched
        → invoice.paid dispatched
T+31s   Webhook delivered to merchant
T+32s   Merchant responds HTTP 200
        → Delivery logged as "delivered"

──────────────────────────────────────────

Customer sends 60 USDT for a 100 USDT invoice:

T+0s    Invoice created (status: pending)
T+30s   Transaction confirmed
        → payment.confirmed dispatched
        → invoice.underpaid dispatched
        (remaining: 40.0 USDT)
T+120s  Customer sends 40 USDT top-up
T+150s  Second transaction confirmed
        → payment.confirmed dispatched
        → invoice.paid dispatched`} />

            <InfoBox type="info" title="Subscribing to Events">
              When creating a webhook in the dashboard, select only the events you need. This reduces noise and improves your endpoint's processing efficiency. You can always add more events later.
            </InfoBox>
          </Section>

          {/* IMPLEMENTATION EXAMPLES */}
          <Section id="implementation" title="Implementation Examples">
            <p>Production-ready code examples for common integration patterns.</p>

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Webhook Handler — Node.js (Express)</h3>
            <CodeBlock title="Complete Express Webhook Handler" lang="javascript" code={`const express = require('express');
const crypto = require('crypto');
const app = express();

const WEBHOOK_SECRET = process.env.COINPAYUP_WEBHOOK_SECRET;

// Use raw body for signature verification
app.post('/webhooks/coinpayup', express.raw({ type: 'application/json' }), async (req, res) => {
  const signature = req.headers['x-coinpayup-signature'];
  const eventType = req.headers['x-coinpayup-event'];
  
  // Step 1: Verify signature
  const payload = JSON.parse(req.body.toString());
  const serialized = JSON.stringify(payload, Object.keys(payload).sort());
  const expected = crypto.createHmac('sha256', WEBHOOK_SECRET)
    .update(serialized).digest('hex');
  
  if (!crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected))) {
    console.error('Invalid webhook signature');
    return res.status(401).json({ error: 'Invalid signature' });
  }
  
  // Step 2: Respond immediately with 200
  res.status(200).json({ received: true });
  
  // Step 3: Process event asynchronously
  try {
    const { event, data } = payload;
    
    switch (event) {
      case 'invoice.paid':
        await fulfillOrder(data.invoice_id, data.amount, data.currency);
        break;
      case 'invoice.underpaid':
        await notifyPartialPayment(data.invoice_id, data.remaining_amount);
        break;
      case 'invoice.overpaid':
        await flagForReview(data.invoice_id, data.overpaid_by);
        break;
      case 'invoice.expired':
        await cancelPendingOrder(data.invoice_id);
        break;
    }
  } catch (err) {
    console.error('Webhook processing error:', err);
  }
});`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Webhook Handler — Python (FastAPI)</h3>
            <CodeBlock title="Complete FastAPI Webhook Handler" lang="python" code={`from fastapi import FastAPI, Request, HTTPException
import hmac, hashlib, json

app = FastAPI()
WEBHOOK_SECRET = os.environ["COINPAYUP_WEBHOOK_SECRET"]

@app.post("/webhooks/coinpayup")
async def handle_webhook(request: Request):
    body = await request.body()
    signature = request.headers.get("X-CoinPayUp-Signature", "")
    
    # Step 1: Verify signature
    payload = json.loads(body)
    serialized = json.dumps(payload, sort_keys=True)
    expected = hmac.new(
        WEBHOOK_SECRET.encode(), serialized.encode(), hashlib.sha256
    ).hexdigest()
    
    if not hmac.compare_digest(signature, expected):
        raise HTTPException(status_code=401, detail="Invalid signature")
    
    # Step 2: Process event
    event = payload["event"]
    data = payload["data"]
    
    if event == "invoice.paid":
        await fulfill_order(data["invoice_id"])
    elif event == "invoice.underpaid":
        await notify_partial_payment(data["invoice_id"], data["remaining_amount"])
    elif event == "invoice.expired":
        await cancel_pending_order(data["invoice_id"])
    
    return {"received": True}`} />

            <h3 className="text-lg font-semibold text-foreground mt-8 mb-3">Redirect Handling — Frontend</h3>
            <CodeBlock title="React Success Page Example" lang="javascript" code={`// pages/PaymentSuccess.jsx
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  
  useEffect(() => {
    const invoiceId = searchParams.get('invoice_id');
    if (invoiceId) {
      // Verify payment status server-side — never trust the redirect alone
      fetch(\`/api/invoices/\${invoiceId}/status\`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 'paid') {
            setInvoice(data);
          } else {
            // Payment not actually confirmed — redirect to checkout
            window.location.href = \`/checkout/\${invoiceId}\`;
          }
        });
    }
  }, [searchParams]);
  
  if (!invoice) return <div>Verifying payment...</div>;
  
  return (
    <div>
      <h1>Payment Confirmed!</h1>
      <p>Amount: {invoice.amount} {invoice.currency}</p>
      <p>Invoice: {invoice.id}</p>
    </div>
  );
}`} />

            <InfoBox type="success" title="Key Pattern">
              Notice how the success page verifies the payment server-side before showing the confirmation. This protects against users manually navigating to the success URL without actually paying.
            </InfoBox>
          </Section>

          {/* BEST PRACTICES */}
          <Section id="best-practices" title="Best Practices">
            <div className="space-y-4">
              {[
                { title: 'Always verify webhook signatures', desc: 'Every webhook request must be verified using HMAC-SHA256 before processing. This is your primary defense against spoofed payment notifications.' },
                { title: 'Handle duplicate events safely', desc: 'CoinPayUp may deliver the same event more than once (during retries). Use the invoice_id + event type as an idempotency key to avoid processing the same event twice.' },
                { title: 'Respond with HTTP 200 immediately', desc: 'Return a 200 response as fast as possible — before doing heavy processing. Queue the webhook payload for asynchronous processing to avoid timeouts and unnecessary retries.' },
                { title: 'Log all webhook events', desc: 'Store every webhook payload in your database for auditing, debugging, and dispute resolution. Include the timestamp, event type, and full payload.' },
                { title: 'Don\'t rely solely on browser redirects', desc: 'Redirects are a UX convenience, not a payment confirmation. Always use webhooks or server-side API polling to verify payment status before fulfilling orders.' },
                { title: 'Monitor your webhook delivery logs', desc: 'Regularly check your webhook delivery history in the CoinPayUp dashboard. Failed deliveries indicate problems with your endpoint that need attention.' },
                { title: 'Use HTTPS endpoints only', desc: 'CoinPayUp only delivers webhooks to HTTPS URLs. Ensure your endpoint has a valid SSL certificate and is publicly accessible.' },
                { title: 'Handle all event types gracefully', desc: 'Even if you only care about invoice.paid, your handler should gracefully ignore unknown event types rather than returning errors that trigger retries.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 p-4 rounded-xl bg-muted/30 border border-border">
                  <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* COMMON MISTAKES */}
          <Section id="common-mistakes" title="Common Integration Mistakes">
            <p>Avoid these common pitfalls when integrating CoinPayUp payment notifications.</p>

            <div className="space-y-4 mt-6">
              {[
                {
                  title: 'Relying only on browser redirects',
                  problem: 'Merchant fulfills orders based on the customer landing on the success URL.',
                  risk: 'Anyone can navigate to the success URL manually. The customer might also close the browser before the redirect fires.',
                  fix: 'Always verify payment status server-side using webhooks or the invoice API before fulfilling orders.',
                },
                {
                  title: 'Not verifying webhook signatures',
                  problem: 'Merchant processes all incoming POST requests to their webhook endpoint without checking the signature.',
                  risk: 'An attacker can send fake payment.confirmed events to your endpoint, tricking your system into fulfilling unpaid orders.',
                  fix: 'Implement HMAC-SHA256 signature verification for every incoming webhook. Reject any request with an invalid or missing signature.',
                },
                {
                  title: 'Ignoring retry behavior',
                  problem: 'Merchant\'s webhook endpoint returns 500 errors intermittently, triggering retries that are processed as new events.',
                  risk: 'The same order gets fulfilled multiple times, or inventory counts are decremented incorrectly.',
                  fix: 'Implement idempotency checks using the invoice_id and event type. Track processed events in your database and skip duplicates.',
                },
                {
                  title: 'Not handling partial payments',
                  problem: 'Merchant only listens for invoice.paid and ignores invoice.underpaid events.',
                  risk: 'Customer sends a partial payment and sees no feedback. They may think the payment failed and re-pay, or the order stays in limbo indefinitely.',
                  fix: 'Subscribe to invoice.underpaid events. Notify the customer of the remaining balance and provide clear instructions for the top-up payment.',
                },
                {
                  title: 'Slow webhook response times',
                  problem: 'Merchant performs order fulfillment, sends emails, and updates the database synchronously inside the webhook handler before returning a response.',
                  risk: 'The response takes longer than 30 seconds, CoinPayUp treats it as a failure, and retries the webhook — causing duplicate processing.',
                  fix: 'Return HTTP 200 immediately, then process the event asynchronously using a job queue (Redis, RabbitMQ, SQS, etc.).',
                },
              ].map((item, i) => (
                <div key={i} className="rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-3 bg-red-500/5 border-b border-border flex items-center gap-2">
                    <AlertOctagon className="h-4 w-4 text-red-400" />
                    <span className="text-sm font-semibold text-foreground">{item.title}</span>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="text-sm">
                      <span className="font-medium text-muted-foreground">Problem: </span>
                      <span className="text-muted-foreground">{item.problem}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-red-400">Risk: </span>
                      <span className="text-muted-foreground">{item.risk}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-emerald-400">Fix: </span>
                      <span className="text-muted-foreground">{item.fix}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <InfoBox type="success" title="Integration Checklist">
              Before going live, verify that your integration:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Verifies HMAC-SHA256 signatures on every webhook</li>
                <li>Returns HTTP 200 within 5 seconds</li>
                <li>Handles duplicate events idempotently</li>
                <li>Processes all relevant event types (paid, underpaid, expired, overpaid)</li>
                <li>Logs all webhook events for auditing</li>
                <li>Does not rely solely on browser redirects for order fulfillment</li>
              </ul>
            </InfoBox>
          </Section>

        </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default DocsNotificationsPage;
